import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { FC, useEffect, useMemo, useState } from 'react';

import { createColumnDependencies } from 'components/ModelList/columns';
import { useURLFilters } from 'hooks/useURLFilters';
import { useURLSearchParams } from 'hooks/useURLSearchParams';

import { FilterIds } from '..';
import { useFilterExtras } from '../shared/FilterContext';

export const dependencies = createColumnDependencies((ids) => [ids.TAGS]);

type InnerProps = { columnId: string };

const Inner: FC<InnerProps> = () => {
  const { updateFilterModelItems } = useURLFilters();
  const [searchParams] = useURLSearchParams();
  const { tagOptions } = useFilterExtras();

  const idByName = useMemo(() => {
    const map = new Map<string, string>();
    (tagOptions ?? []).forEach((t) => map.set(t.name, t.id));
    return map;
  }, [tagOptions]);

  const allNames: string[] = useMemo(
    () => (tagOptions ?? []).map((t) => t.name),
    [tagOptions]
  );

  const urlTags = (searchParams[FilterIds.TAGS] ?? null) as string | null;

  const currentNames = useMemo<string[]>(() => {
    if (!tagOptions?.length || !urlTags) return [];
    const ids = urlTags.split(',').filter(Boolean);
    const names: string[] = [];
    ids.forEach((id) => {
      const found = tagOptions.find((t) => t.id === id);
      if (found) names.push(found.name);
    });
    return names;
  }, [urlTags, tagOptions]);

  const [value, setValue] = useState<string[]>(currentNames);

  useEffect(() => {
    setValue(currentNames);
  }, [currentNames]);

  return (
    <Stack p={1}>
      <Autocomplete<string, true, false, false>
        multiple
        options={allNames}
        value={value}
        onChange={(_, values) => {
          setValue(values);
          const ids = values
            .map((name) => idByName.get(name))
            .filter((v): v is string => Boolean(v));
          const next = ids.length ? ids.join(',') : null;

          if (urlTags === next) return; // guard
          updateFilterModelItems({ [FilterIds.TAGS]: next });
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
              <Chip
                key={key ?? `tag-${option}-${index}`}
                variant="outlined"
                label={option}
                {...chipProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField {...params} label="Tags" size="small" />
        )}
      />
    </Stack>
  );
};

const TagsMultiSelect: FC = () => {
  const [TAGS] = dependencies;
  if (!TAGS) return null;

  return <Inner columnId={TAGS} />;
};

export default TagsMultiSelect;
